"use client";

import { getCourseEmployeeDetailApiV1AdminCoursesCourseRefEmployeesUserIdGet } from "@/src/api/sdk.gen";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, AlertTriangle, BookOpen } from "lucide-react";

interface Employee {
    userId: number;
    name: string;
    email: string | null;
    imageUrl: string | null;
    lastActiveAt: string | null;
    enrolledAt: string;
    currentLevel: string | null;
    progressPercentage: number;
}

interface QuizAttempt {
    moduleTitle: string;
    levelName: string | null;
    score: number;
    passed: boolean;
    submittedAt: string;
}

interface DetailData {
    currentLevel: string;
    placementLevel: string;
    quizPerformance: QuizAttempt[];
    remedialTotal: number;
    remedialBreakdown: Array<{ lessonTitle: string; count: number }>;
}

interface EmployeeDetailPanelProps {
    courseUuid: string;
    employee: Employee;
    struggledConcepts: string[];
    onClose: () => void;
}

export function EmployeeDetailPanel({
    courseUuid,
    employee,
    struggledConcepts,
    onClose,
}: EmployeeDetailPanelProps) {
    const [detail, setDetail] = useState<DetailData | null>(null);

    useEffect(() => {
        let cancelled = false;
        getCourseEmployeeDetailApiV1AdminCoursesCourseRefEmployeesUserIdGet({
            path: { course_ref: courseUuid, user_id: employee.userId },
        })
            .then(({ data, error }: { data: any; error: any }) => {
                if (error || !data) {
                    throw new Error("Failed to load employee detail");
                }
                if (!cancelled) setDetail(data as unknown as DetailData);
            })
            .catch((err: any) => console.error("Failed to load detail:", err));
        return () => {
            cancelled = true;
        };
    }, [courseUuid, employee.userId]);

    const detailLoading = detail === null;

    const formatDate = (dateString: string | null) => {
        if (!dateString) return "Never";
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 60) return `${diffMins}m ago`;
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    return createPortal(
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] transition-opacity"
                onClick={onClose}
            />

            {/* Panel */}
            <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-[70] overflow-y-auto animate-in slide-in-from-right duration-200">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
                    <h2 className="text-sm font-bold text-slate-900">Student Details</h2>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Profile — instant, from list data */}
                    <div className="flex items-center gap-4">
                        {employee.imageUrl ? (
                            <img
                                src={employee.imageUrl}
                                alt=""
                                className="h-12 w-12 rounded-full bg-slate-100 object-cover"
                            />
                        ) : (
                            <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center text-lg font-bold text-indigo-600">
                                {employee.name.charAt(0).toUpperCase()}
                            </div>
                        )}
                        <div>
                            <h3 className="text-base font-bold text-slate-900">
                                {employee.name}
                            </h3>
                            <p className="text-xs text-slate-500">{employee.email}</p>
                        </div>
                    </div>

                    {/* Meta Info — instant */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="rounded-lg bg-slate-50 p-3">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                                Enrolled
                            </p>
                            <p className="text-sm font-bold text-slate-900 mt-1">
                                {new Date(employee.enrolledAt).toLocaleDateString()}
                            </p>
                        </div>
                        <div className="rounded-lg bg-slate-50 p-3">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                                Last Active
                            </p>
                            <p className="text-sm font-bold text-slate-900 mt-1">
                                {formatDate(employee.lastActiveAt)}
                            </p>
                        </div>
                    </div>

                    {/* Placement & Current Level */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="rounded-lg bg-slate-50 p-3">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                                Placement Level
                            </p>
                            {detailLoading ? (
                                <div className="h-5 w-24 bg-slate-200 rounded animate-pulse mt-1" />
                            ) : (
                                <p className="text-sm font-bold mt-1 text-slate-900">
                                    {detail?.placementLevel || "Unknown"}
                                </p>
                            )}
                        </div>
                        <div className="rounded-lg bg-slate-50 p-3">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                                Current Level
                            </p>
                            {detailLoading ? (
                                <div className="h-5 w-24 bg-slate-200 rounded animate-pulse mt-1" />
                            ) : (
                                <p className={`text-sm font-bold mt-1 ${
                                    detail?.currentLevel === "Completed" ? "text-green-600" : "text-slate-900"
                                }`}>
                                    {detail?.currentLevel || "Unknown"}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Quiz Performance — fetched in background */}
                    <div>
                        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">
                            Quiz Performance
                        </h4>
                        {detailLoading ? (
                            <div className="space-y-2">
                                {[1, 2].map((i) => (
                                    <div key={i} className="h-10 bg-slate-100 rounded-lg animate-pulse" />
                                ))}
                            </div>
                        ) : !detail?.quizPerformance.length ? (
                            <p className="text-xs text-slate-400 italic">No quiz attempts yet</p>
                        ) : (
                            <div className="space-y-2">
                                {detail.quizPerformance.map((quiz, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5"
                                    >
                                        <div className="flex-1 min-w-0 mr-3">
                                            <p className="text-xs font-medium text-slate-700 truncate">
                                                {quiz.levelName && (
                                                    <span className="text-slate-400">
                                                        {quiz.levelName} -{" "}
                                                    </span>
                                                )}
                                                {quiz.moduleTitle}
                                            </p>
                                        </div>
                                        <span
                                            className={`text-sm font-bold flex items-center gap-1 ${
                                                quiz.score >= 70
                                                    ? "text-green-600"
                                                    : quiz.score >= 50
                                                    ? "text-amber-600"
                                                    : "text-red-600"
                                            }`}
                                        >
                                            {quiz.score}%
                                            {quiz.score < 50 && (
                                                <AlertTriangle className="h-3 w-3" />
                                            )}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Struggled Concepts — pre-computed from employee-stats, no extra fetch */}
                    <div>
                        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">
                            Struggled Concepts
                        </h4>
                        {!struggledConcepts.length ? (
                            <p className="text-xs text-slate-400 italic">No struggled concepts</p>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {struggledConcepts.map((concept) => (
                                    <span
                                        key={concept}
                                        className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700"
                                    >
                                        <AlertTriangle className="h-3 w-3" />
                                        {concept}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Remedial Lessons */}
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <BookOpen className="h-3.5 w-3.5 text-amber-500" />
                            <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                                Remedial Lessons
                            </h4>
                            {!detailLoading && detail && (
                                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                                    detail.remedialTotal > 5
                                        ? "bg-red-100 text-red-700"
                                        : detail.remedialTotal > 0
                                        ? "bg-amber-100 text-amber-700"
                                        : "bg-slate-100 text-slate-500"
                                }`}>
                                    {detail.remedialTotal} total
                                </span>
                            )}
                        </div>
                        {detailLoading ? (
                            <div className="space-y-2">
                                {[1, 2].map((i) => (
                                    <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />
                                ))}
                            </div>
                        ) : !detail?.remedialBreakdown.length ? (
                            <p className="text-xs text-slate-400 italic">No remedial lessons generated</p>
                        ) : (
                            <div className="space-y-2">
                                {detail.remedialBreakdown.map((item) => (
                                    <div
                                        key={item.lessonTitle}
                                        className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5"
                                    >
                                        <p className="text-xs font-medium text-slate-700 truncate mr-3">
                                            {item.lessonTitle}
                                        </p>
                                        <span className="text-xs font-medium text-amber-600 flex-shrink-0">
                                            {item.count} generated
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
}
